import { useCallback, useEffect } from 'react';
import { T200 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { RequestAccessTokenMethod, ThirdPartyEntity } from '../../../../common/types';
import { useContentForThirdParty } from '../../../../common/utils';
import { useThirdPartyState } from '../../../../controllers/import-state';
import { useThirdPartyWorkspaces } from '../../../../services/third-party-workspaces';

type Props = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
	hideWorkspaceSelector?: boolean;
};

export const useWorkspaces = ({
	thirdParty,
	shouldShowPlaceholderIcons,
	requestAuthenticationToken,
	hideWorkspaceSelector,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		workspacesSelectorLabel: label,
		workspacesSelectorPlaceholder: placeholder,
		starredWorkspacesLabel: starredOptionsLabel,
		otherWorkspacesLabel: otherOptionsLabel,
		noWorkspacesFoundHeader: noEntitiesFoundHeader,
		noWorkspacesFoundDescription: noEntitiesFoundDescription,
		errorOccurredFetchingWorkspacesHeader: errorOccurredHeader,
	} = useContentForThirdParty(thirdParty);

	const { fetchThirdPartyWorkspaces, hasErrorOccurred, isLoading } = useThirdPartyWorkspaces({
		thirdParty,
		requestAuthenticationToken,
	});

	const [
		{ workspaces: entities, selectedWorkspaceId },
		{ setThirdPartyState, setThirdPartyStateWithPreselectedWorkspaceId },
	] = useThirdPartyState();

	const onFetchWorkspaces = useCallback(() => {
		fetchThirdPartyWorkspaces().then((workspaceEntities) => {
			setThirdPartyStateWithPreselectedWorkspaceId({
				workspaces: workspaceEntities,
				preSelectedWorkspaceId: selectedWorkspaceId,
			});
			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyWorkspaces retrieved', {
				thirdParty,
				numWorkspaces: workspaceEntities?.length || 0,
			});
		});
	}, [
		createAnalyticsEvent,
		fetchThirdPartyWorkspaces,
		selectedWorkspaceId,
		setThirdPartyStateWithPreselectedWorkspaceId,
		thirdParty,
	]);

	useEffect(() => {
		!hideWorkspaceSelector && !entities && onFetchWorkspaces();
	}, [entities, onFetchWorkspaces, hideWorkspaceSelector]);

	// TODO: Remove this on cleanup of 'jira_importer_third_party_state_refactor_2' feature gate
	const onChangeOld = useCallback(
		(workspaceId: string) => {
			setThirdPartyState({
				selectedWorkspaceId: workspaceId,
				selectedSpaceId: null,
				selectedSpaceName: null,
				selectedFolderId: null,
				selectedSubfolderId: null,
				selectedEntities: null,
				selectedEntityId: null,
				selectedEntityName: null,
				selectedEntityStyle: null,
				spaces: null,
				folders: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	const onChange = useCallback(
		(selectedWorkspace: ThirdPartyEntity) => {
			setThirdPartyState({
				selectedWorkspaceId: selectedWorkspace.id,
				selectedSpaceId: null,
				selectedSpaceName: null,
				selectedFolderId: null,
				selectedSubfolderId: null,
				selectedEntities: null,
				selectedEntityId: null,
				selectedEntityName: null,
				selectedEntityStyle: null,
				spaces: null,
				folders: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	return {
		label,
		placeholder,
		starredOptionsLabel,
		otherOptionsLabel,
		noEntitiesFoundHeader,
		noEntitiesFoundDescription,
		errorOccurredHeader,
		entities,
		selectedEntityId: selectedWorkspaceId,
		isLoading,
		hasErrorOccurred,
		onChangeOld,
		onChange,
		onRetry: onFetchWorkspaces,
		autoFocus: !selectedWorkspaceId,
		openMenuOnFocus: true,
		placeholderColor: shouldShowPlaceholderIcons ? T200 : undefined,
		preselectOnlyOption: true,
	};
};
